import type { ActivityContext, ActivityHandle } from '../../core/activity';
import levelsData from './levels.json';
import { generateGrid, evaluate, parseQmlOperator, type Expression, type Operator } from './generator';

interface SubLevel {
  goal: number;
  operator: string;
  spawnMonsters: boolean;
}

interface Level {
  level: number;
  objective: string;
  difficulty: number;
  data: SubLevel[];
}

interface LevelsFile {
  levels: Level[];
}

const LEVELS = (levelsData as LevelsFile).levels;
const GRID_SIZE = 6;
const TOTAL_CELLS = GRID_SIZE * GRID_SIZE;
const CELL_SIZE = 60;
const MAX_LIVES = 3;
const MAX_MONSTERS = 3;
const MONSTER_MOVE_INTERVAL = 1400;
const MONSTER_SPAWN_MIN = 3500;
const MONSTER_SPAWN_MAX = 8000;

const OBJECTIVE_PT: Record<string, string> = {
  'Practice addition equality.': 'Treine igualdade na adição.',
  'Practice subtraction equality.': 'Treine igualdade na subtração.',
  'Practice multiplication equality.': 'Treine igualdade na multiplicação.',
  'Practice division equality.': 'Treine igualdade na divisão.',
};

type Direction = 'up' | 'down' | 'left' | 'right';

interface Monster {
  pos: number;
}

interface CellState {
  expr: Expression;
  eaten: boolean;
}

export function mountGnumchEquality(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }

  const objectivePt = OBJECTIVE_PT[level.objective] ?? level.objective;
  const totalSubs = level.data.length;

  let subIndex = 0;
  let correctSubs = 0;
  let cells: CellState[] = [];
  let muncherPos = 0;
  let lives = MAX_LIVES;
  let monsters: Monster[] = [];
  let buttonsBlocked = false;
  let monsterMoveTimer: number | null = null;
  let monsterSpawnTimer: number | null = null;
  let flashTimer: number | null = null;
  let currentGoal = 0;
  let currentOp: Operator = '+';
  let withMonsters = false;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Comilão de números</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage gnumch">
      <div class="feedback" aria-live="polite"></div>
      <div class="gnumch__header">
        <p class="gnumch__progress" aria-live="polite"></p>
        <span class="gnumch__lives" aria-live="polite"></span>
      </div>
      <div class="gnumch__goal" aria-live="polite"></div>
      <div class="gnumch__grid-wrap"></div>
      <p class="gnumch__hint">${objectivePt} · Toque numa célula vizinha pra mover, na sua célula pra comer.</p>
    </section>
  `;
  ctx.root.appendChild(page);

  const progressEl = page.querySelector<HTMLElement>('.gnumch__progress')!;
  const livesEl = page.querySelector<HTMLElement>('.gnumch__lives')!;
  const goalEl = page.querySelector<HTMLElement>('.gnumch__goal')!;
  const gridWrap = page.querySelector<HTMLElement>('.gnumch__grid-wrap')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());

  const onKey = (e: KeyboardEvent): void => {
    if (buttonsBlocked) return;
    if (e.key === 'ArrowUp') { tryMove('up'); e.preventDefault(); }
    else if (e.key === 'ArrowDown') { tryMove('down'); e.preventDefault(); }
    else if (e.key === 'ArrowLeft') { tryMove('left'); e.preventDefault(); }
    else if (e.key === 'ArrowRight') { tryMove('right'); e.preventDefault(); }
    else if (e.key === ' ' || e.key === 'Enter') { eatCurrent(); e.preventDefault(); }
  };
  window.addEventListener('keydown', onKey);

  function startSubLevel(): void {
    clearTimers();
    const cfg = level.data[subIndex]!;
    currentGoal = cfg.goal;
    currentOp = parseQmlOperator(cfg.operator);
    withMonsters = cfg.spawnMonsters;
    const exprs = generateGrid(currentOp, currentGoal, TOTAL_CELLS);
    cells = exprs.map((expr) => ({ expr, eaten: false }));
    muncherPos = Math.floor(TOTAL_CELLS / 2) + Math.floor(GRID_SIZE / 2); // ~middle
    lives = MAX_LIVES;
    monsters = [];
    buttonsBlocked = false;
    renderAll();
    if (withMonsters) {
      monsterMoveTimer = window.setInterval(tickMonsters, MONSTER_MOVE_INTERVAL);
      scheduleMonsterSpawn();
    }
  }

  function clearTimers(): void {
    if (monsterMoveTimer !== null) { window.clearInterval(monsterMoveTimer); monsterMoveTimer = null; }
    if (monsterSpawnTimer !== null) { window.clearTimeout(monsterSpawnTimer); monsterSpawnTimer = null; }
    if (flashTimer !== null) { window.clearTimeout(flashTimer); flashTimer = null; }
  }

  function scheduleMonsterSpawn(): void {
    const delay = MONSTER_SPAWN_MIN + Math.random() * (MONSTER_SPAWN_MAX - MONSTER_SPAWN_MIN);
    monsterSpawnTimer = window.setTimeout(() => {
      if (!withMonsters || buttonsBlocked) return;
      spawnMonster();
      scheduleMonsterSpawn();
    }, delay);
  }

  function spawnMonster(): void {
    if (monsters.length >= MAX_MONSTERS) return;
    const edges = [
      ...Array.from({ length: GRID_SIZE }, (_, i) => i), // top row
      ...Array.from({ length: GRID_SIZE }, (_, i) => i + (GRID_SIZE - 1) * GRID_SIZE), // bottom row
      ...Array.from({ length: GRID_SIZE }, (_, i) => i * GRID_SIZE), // left col
      ...Array.from({ length: GRID_SIZE }, (_, i) => i * GRID_SIZE + GRID_SIZE - 1), // right col
    ];
    const pos = edges[Math.floor(Math.random() * edges.length)]!;
    if (pos === muncherPos) return;
    monsters.push({ pos });
    if (pos === muncherPos) {
      onCaught();
      return;
    }
    renderAll();
  }

  function tickMonsters(): void {
    if (buttonsBlocked) return;
    for (const m of monsters) {
      const dirs: Direction[] = ['up', 'down', 'left', 'right'];
      const shuffled = dirs.slice().sort(() => Math.random() - 0.5);
      for (const d of shuffled) {
        const next = neighbor(m.pos, d);
        if (next !== -1) {
          m.pos = next;
          break;
        }
      }
    }
    if (monsters.some((m) => m.pos === muncherPos)) {
      onCaught();
      return;
    }
    renderAll();
  }

  function neighbor(pos: number, dir: Direction): number {
    const row = Math.floor(pos / GRID_SIZE);
    const col = pos % GRID_SIZE;
    if (dir === 'up') return row > 0 ? pos - GRID_SIZE : -1;
    if (dir === 'down') return row < GRID_SIZE - 1 ? pos + GRID_SIZE : -1;
    if (dir === 'left') return col > 0 ? pos - 1 : -1;
    return col < GRID_SIZE - 1 ? pos + 1 : -1;
  }

  function isAdjacent(a: number, b: number): boolean {
    const r1 = Math.floor(a / GRID_SIZE);
    const c1 = a % GRID_SIZE;
    const r2 = Math.floor(b / GRID_SIZE);
    const c2 = b % GRID_SIZE;
    return Math.abs(r1 - r2) + Math.abs(c1 - c2) === 1;
  }

  function tryMove(dir: Direction): void {
    if (buttonsBlocked) return;
    const next = neighbor(muncherPos, dir);
    if (next === -1) return;
    muncherPos = next;
    ctx.audio.play('click');
    if (monsters.some((m) => m.pos === muncherPos)) {
      onCaught();
      return;
    }
    renderAll();
  }

  function eatCurrent(): void {
    if (buttonsBlocked) return;
    const cell = cells[muncherPos]!;
    if (cell.eaten) return;
    const value = evaluate(cell.expr);
    const correct = value === currentGoal;
    cell.eaten = true;
    if (correct) {
      ctx.audio.play('correct');
      flashCell(muncherPos, 'correct');
      if (allCorrectEaten()) {
        buttonsBlocked = true;
        clearTimers();
        window.setTimeout(() => {
          subIndex++;
          correctSubs++;
          if (subIndex < totalSubs) startSubLevel();
          else finishLevel();
        }, 500);
      } else {
        renderAll();
      }
    } else {
      ctx.audio.play('wrong');
      lives--;
      flashCell(muncherPos, 'wrong');
      if (lives <= 0) {
        onAllLivesLost();
      } else {
        renderAll();
      }
    }
  }

  function flashCell(pos: number, kind: 'correct' | 'wrong'): void {
    renderAll();
    const cellEl = gridWrap.querySelector<SVGElement>(`rect[data-id="${pos}"]`);
    if (cellEl) {
      cellEl.classList.add(`gnumch__cell-bg--${kind}`);
    }
    if (flashTimer !== null) window.clearTimeout(flashTimer);
    flashTimer = window.setTimeout(() => {
      flashTimer = null;
      if (cellEl) cellEl.classList.remove(`gnumch__cell-bg--${kind}`);
    }, 350);
  }

  function allCorrectEaten(): boolean {
    return cells.every((c) => c.eaten || evaluate(c.expr) !== currentGoal);
  }

  function onCaught(): void {
    if (buttonsBlocked) return;
    lives--;
    ctx.audio.play('wrong');
    showFeedback('err');
    if (lives <= 0) {
      onAllLivesLost();
    } else {
      monsters = [];
      muncherPos = Math.floor(TOTAL_CELLS / 2) + Math.floor(GRID_SIZE / 2);
      renderAll();
    }
  }

  function onAllLivesLost(): void {
    buttonsBlocked = true;
    clearTimers();
    showFeedback('err');
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    window.setTimeout(() => {
      stage.innerHTML = `
        <h2 class="activity-finish__title">Você perdeu as 3 vidas</h2>
        <p class="activity-finish__text">Sub-nível ${subIndex + 1} de ${totalSubs}. Tente de novo!</p>
        <div class="activity-finish__actions">
          <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
          <button class="btn btn--primary" type="button" data-act="retry">Tentar de novo</button>
        </div>
      `;
      stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
      stage.querySelector<HTMLElement>('[data-act="retry"]')!.addEventListener('click', () => {
        rebuildPage();
        startSubLevel();
      });
    }, 800);
  }

  function rebuildPage(): void {
    ctx.root.innerHTML = '';
    page.innerHTML = `
      <header class="activity-bar">
        <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
        <div class="activity-bar__title">Comilão de números</div>
        <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
      </header>
      <section class="activity-stage gnumch">
        <div class="feedback" aria-live="polite"></div>
        <div class="gnumch__header">
          <p class="gnumch__progress" aria-live="polite"></p>
          <span class="gnumch__lives" aria-live="polite"></span>
        </div>
        <div class="gnumch__goal" aria-live="polite"></div>
        <div class="gnumch__grid-wrap"></div>
        <p class="gnumch__hint">${objectivePt} · Toque numa célula vizinha pra mover, na sua célula pra comer.</p>
      </section>
    `;
    ctx.root.appendChild(page);
    bindRefs();
  }

  let progressElRef: HTMLElement = progressEl;
  let livesElRef: HTMLElement = livesEl;
  let goalElRef: HTMLElement = goalEl;
  let gridWrapRef: HTMLElement = gridWrap;
  let feedbackRef: HTMLElement = feedback;

  function bindRefs(): void {
    progressElRef = page.querySelector<HTMLElement>('.gnumch__progress')!;
    livesElRef = page.querySelector<HTMLElement>('.gnumch__lives')!;
    goalElRef = page.querySelector<HTMLElement>('.gnumch__goal')!;
    gridWrapRef = page.querySelector<HTMLElement>('.gnumch__grid-wrap')!;
    feedbackRef = page.querySelector<HTMLElement>('.feedback')!;
    page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());
  }

  function renderProgress(): void {
    progressElRef.textContent = `Pergunta ${subIndex + 1} de ${totalSubs}`;
    livesElRef.textContent = '❤️'.repeat(lives) + '🤍'.repeat(MAX_LIVES - lives);
    goalElRef.innerHTML = `Coma os que dão <strong>= ${currentGoal}</strong>`;
  }

  function renderAll(): void {
    renderProgress();
    renderGrid();
  }

  function renderGrid(): void {
    const dim = GRID_SIZE * CELL_SIZE;
    gridWrapRef.innerHTML = '';
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', `0 0 ${dim} ${dim}`);
    svg.setAttribute('class', 'gnumch__grid');

    // Cells
    for (let id = 0; id < TOTAL_CELLS; id++) {
      const row = Math.floor(id / GRID_SIZE);
      const col = id % GRID_SIZE;
      const x = col * CELL_SIZE;
      const y = row * CELL_SIZE;
      const cell = cells[id]!;
      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      rect.setAttribute('x', String(x));
      rect.setAttribute('y', String(y));
      rect.setAttribute('width', String(CELL_SIZE));
      rect.setAttribute('height', String(CELL_SIZE));
      let cls = 'gnumch__cell-bg';
      if (cell.eaten) cls += ' gnumch__cell-bg--eaten';
      else if (id === muncherPos) cls += ' gnumch__cell-bg--muncher';
      else if (isAdjacent(id, muncherPos)) cls += ' gnumch__cell-bg--adjacent';
      rect.setAttribute('class', cls);
      rect.setAttribute('data-id', String(id));
      rect.addEventListener('click', () => onCellClick(id));
      svg.appendChild(rect);

      if (!cell.eaten) {
        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        text.setAttribute('x', String(x + CELL_SIZE / 2));
        text.setAttribute('y', String(y + CELL_SIZE / 2));
        text.setAttribute('class', 'gnumch__cell-expr');
        text.textContent = `${cell.expr.a}${cell.expr.op}${cell.expr.b}`;
        svg.appendChild(text);
      }
    }

    // Muncher
    {
      const row = Math.floor(muncherPos / GRID_SIZE);
      const col = muncherPos % GRID_SIZE;
      const x = col * CELL_SIZE + CELL_SIZE / 2;
      const y = row * CELL_SIZE + CELL_SIZE / 2;
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('x', String(x));
      text.setAttribute('y', String(y));
      text.setAttribute('class', 'gnumch__muncher');
      text.textContent = '😋';
      svg.appendChild(text);
    }

    // Monsters
    for (const m of monsters) {
      const row = Math.floor(m.pos / GRID_SIZE);
      const col = m.pos % GRID_SIZE;
      const x = col * CELL_SIZE + CELL_SIZE / 2;
      const y = row * CELL_SIZE + CELL_SIZE / 2;
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('x', String(x));
      text.setAttribute('y', String(y));
      text.setAttribute('class', 'gnumch__monster');
      text.textContent = '👻';
      svg.appendChild(text);
    }

    gridWrapRef.appendChild(svg);
  }

  function onCellClick(id: number): void {
    if (buttonsBlocked) return;
    if (id === muncherPos) {
      eatCurrent();
      return;
    }
    if (isAdjacent(id, muncherPos)) {
      muncherPos = id;
      ctx.audio.play('click');
      if (monsters.some((m) => m.pos === muncherPos)) {
        onCaught();
        return;
      }
      renderAll();
    }
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedbackRef.textContent = kind === 'ok' ? '✓' : '✗';
    feedbackRef.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedbackRef.classList.remove('feedback--show'), 500);
  }

  function finishLevel(): void {
    clearTimers();
    ctx.audio.play('win');
    ctx.onComplete(level.level, correctSubs);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você completou ${correctSubs} de ${totalSubs} desafios.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/gnumch-equality/${level.level + 1}`;
      });
    }
  }

  startSubLevel();

  return {
    unmount(): void {
      clearTimers();
      window.removeEventListener('keydown', onKey);
      ctx.root.innerHTML = '';
    },
  };
}
